// Form 1 - Basic Information

import { useState, useEffect, useRef } from 'react';
import PhoneNumber from 'google-libphonenumber';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import CountryFlag from 'react-country-flag';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import countryCodesData from '../../../../data/countryCodes.json';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, ShieldAlert, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import FileUpload from '@/js/components/FileUpload';
import { fetchExistingDocuments } from '@/js/components/FileUploadBox';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const DOCUMENT_TITLES = {
  brochure: 'Brochure',
  companyDoc: 'Company Document',
  companyPPT: 'Company Presentation',
};
const countryCodes = countryCodesData.data;

const formSchema = z.object({
  cin_number: z.string().min(1, 'CIN number is required.'),
  company_name: z.string().min(1, 'Company name is required.'),
  languages_supported: z.array(z.string()).optional(),
  website: z
    .string()
    .regex(
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid website URL',
    )
    .or(z.literal('')),
  linkedin: z.string().refine(
    (url) => {
      if (url === '') return true;
      try {
        const parsedUrl = new URL(url);
        return (
          parsedUrl.hostname === 'www.linkedin.com' ||
          parsedUrl.hostname === 'linkedin.com'
        );
      } catch {
        return false;
      }
    },
    { message: 'Please enter a valid LinkedIn URL or leave it empty' },
  ),
  address: z.string().min(1, 'Address is required.'),
  pin_code: z.string().min(1, 'Pin code is required.'),
  city: z.string().min(1, 'City is required.'),
  state: z.string().min(1, 'State is required.'),
  country: z.string().min(1, 'Country is required.'),
  countryCode: z
    .string()
    .regex(/^\+?\d+$/, 'Country code must be a valid number'),
  phoneNumber: z
    .string()
    .regex(/^\d+$/, 'Phone number must contain only numbers'),
  name_of_sales_person: z.string().min(1, 'Sales person name is required.'),
  salesPersonCountryCode: z.string().min(1, 'Country code is required.'),
  number_of_sales_person: z.string().min(1, 'Phone number is required.'),
  email_of_sales_person: z.string().email('Invalid email address.'),
});

const BasicInformationScreen = ({ setSectionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const [error, setError] = useState(null);

  const [processing, setProcessing] = useState(false);
  const token = useSelector((state) => state.authReducer.token);

  const languagesSupportedInputRef = useRef(null);

  const [languagesSupported, setLanguagesSupported] = useState(
    Array.isArray(reduxData?.languages_supported)
      ? reduxData.languages_supported
      : reduxData?.languages_supported?.split(',') || [],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      cin_number: reduxData?.cin_number || '',
      company_name: reduxData?.company_name || '',
      website: reduxData?.website || '',
      linkedin: reduxData?.linkedin || '',
      languages_supported: reduxData?.languages_supported
        ? languagesSupported
        : [],
      address: reduxData?.address || '',
      pin_code: reduxData?.pin_code || '',
      city: reduxData?.city || '',
      state: reduxData?.state || '',
      country: reduxData?.country || '',
      countryCode: reduxData?.phoneNumber?.split('-')[0] || '+91',
      phoneNumber:
        reduxData?.phoneNumber && reduxData?.phoneNumber[0] === '+'
          ? reduxData?.phoneNumber?.split('-')[1]
          : '',
      name_of_sales_person: reduxData?.name_of_sales_person || '',
      salesPersonCountryCode:
        reduxData?.number_of_sales_person?.split('-')[0] || '+91',
      number_of_sales_person:
        reduxData?.number_of_sales_person &&
        reduxData?.number_of_sales_person[0] === '+'
          ? reduxData?.number_of_sales_person?.split('-')[1]
          : '',
      email_of_sales_person: reduxData?.email_of_sales_person || '',
    },
  });

  // Utility functions
  // 1.
  const getCountryFlag = (countryCode) => {
    const matchedCountry = countryCodes.find(
      (country) => country.code === countryCode,
    );
    return matchedCountry ? matchedCountry.country : '';
  };
  // 2.
  const detectCountryCode = (country) => {
    const matchedCountry = countryCodes.find(
      (c) => c.name.toLowerCase() === country.toLowerCase(),
    );
    return matchedCountry ? matchedCountry.code : '';
  };
  // 3.
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === 'country') {
        const country = values.country;
        if (country) {
          const matchedCountry = countryCodes.find(
            (c) => c.name.toLowerCase() === country.toLowerCase(),
          );

          if (matchedCountry) {
            form.setValue('countryCode', matchedCountry.code, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // 4.
  const detectCountryCodeFromNumber = (phoneNumber) => {
    const phoneUtil = PhoneNumber.PhoneNumberUtil.getInstance();

    try {
      // Attempt to parse the number
      // Use 'ZZ' as default region to allow international number parsing
      const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber, 'ZZ');

      if (phoneUtil.isValidNumber(parsedNumber)) {
        const countryCode = `+${parsedNumber.getCountryCode()}`;

        const regionCode = phoneUtil.getRegionCodeForNumber(parsedNumber);

        const matchedCountry = countryCodes.find(
          (country) => country.country === regionCode,
        );

        // Immediate updates
        form.setValue('countryCode', countryCode, {
          shouldValidate: true,
          shouldDirty: true,
        });

        // Update country if a match is found
        if (matchedCountry) {
          form.setValue('country', matchedCountry.name, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        return true;
      }
    } catch (error) {
      console.log('Phone number parsing error', error);
    }
    return false;
  };

  const handleTagInput = (e, setter, fieldName) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      e.preventDefault();
      const value = e.target.value.trim();
      setter((prev) => [...new Set([...prev, value])]);
      e.target.value = '';
    }
  };

  const removeTag = (index, setter) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // document upload
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentStates, setDocumentStates] = useState({
    brochure: null,
    companyDoc: null,
    companyPPT: null,
  });
  const [uploadProgress, setUploadProgress] = useState({
    brochure: 0,
    companyDoc: 0,
    companyPPT: 0,
  });

  useEffect(() => {
    fetchExistingDocuments(apiClient, setExistingDocuments, setError);
  }, [token]);

  const onSubmit = async (data) => {
    setError(null);
    const fullPhoneNumber = `${data.countryCode}-${data.phoneNumber}`;
    if (
      !form.getValues('countryCode') ||
      form.getValues('countryCode') === '+91'
    ) {
      const detectedCountryCode = detectCountryCode(data.country);
      if (detectedCountryCode) {
        form.setValue('countryCode', detectedCountryCode);
      }
    }
    const apiData = {
      cin_number: data.cin_number,
      company_name: data.company_name,
      linkedin: data.linkedin,
      website: data.website,
      languages_supported: languagesSupported,
      address: data.address,
      pin_code: data.pin_code,
      city: data.city,
      state: data.state,
      country: data.country,
      contact_information: fullPhoneNumber,
      name_of_sales_person: data.name_of_sales_person,
      number_of_sales_person: `${data.salesPersonCountryCode}-${data.number_of_sales_person}`,
      email_of_sales_person: data.email_of_sales_person,
      basic_info_document_ids: [],
    };

    console.log('Submitting to API: ', apiData);

    setProcessing(true);

    try {
      const response = await apiClient.put(
        '/api/manufacturer-profile/basic-info',
        JSON.stringify(apiData),
      );

      const responseData = response.data;
      console.log('API response:', responseData);

      const submissionData = {
        ...data,
        phoneNumber: fullPhoneNumber,
        number_of_sales_person: `${data.salesPersonCountryCode}-${data.number_of_sales_person}`,
      };
      delete submissionData.countryCode;
      delete submissionData.salesPersonCountryCode;
      dispatch(registerSupplierAction(submissionData));
      setSectionId(2);
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.error('Error: ', error_message);

      if (error_message) {
        // Show specific error message
        setError(`Your ${error_message} please login again`);
      } else {
        setError(
          error_message ||
            'A network error occurred. Please check your connection and try again.',
        );
      }
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className="w-full px-20 rounded-md font-roboto text-gray-700">
      <h2 className="text-3xl font-bold text-black text-center mb-4">
        Basic Information
      </h2>
      <p className="text-center text-[#637381] mb-8 text-base">
        This helps us create your verified business profile and connect you with
        buyers.
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="cin_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CIN Number</FormLabel>
                <FormControl>
                  <Input
                    className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    placeholder="Enter CIN Number"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input
                    className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    placeholder="Enter company name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="customerwebsite.com"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="linkedin"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>LinkedIn</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="url"
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Paste your LinkedIn URL"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0  text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="languages_supported"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Languages Supported{' '}
                  <span className="text-gray-400 ">( Press enter to add )</span>
                </FormLabel>
                <FormControl>
                  <div className="w-full">
                    <div className="relative">
                      <Input
                        placeholder="Enter the languages supported by the company"
                        onKeyDown={(e) =>
                          handleTagInput(
                            e,
                            setLanguagesSupported,
                            'languages_supported',
                          )
                        }
                        ref={languagesSupportedInputRef}
                        className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = languagesSupportedInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault() {},
                              },
                              setLanguagesSupported,
                              'languages_supported',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {languagesSupported.map((service, index) => (
                        <div
                          key={service}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {service}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setLanguagesSupported,
                                'languages_supported',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="brochure"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground  ml-2 font-semibold text-gray-700">
                    Add Brochure
                  </FormLabel>
                  <FormControl>
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      key="brochure"
                      id="brochure"
                      label="Upload Brochure"
                      documentType="brochure"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'brochure',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyDoc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-semibold  ml-2 text-gray-700">
                    Add Document of Company
                  </FormLabel>
                  <FormControl>
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      key="companyDoc"
                      id="companyDoc"
                      label="Upload Company doc"
                      documentType="companyDoc"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'companyDoc',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyPPT"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-semibold ml-2 text-gray-700">
                    Add Company Presentation
                  </FormLabel>
                  <FormControl>
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      key="companyPPT"
                      id="companyPPT"
                      label="Upload Company PPT"
                      documentType="companyPPT"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'companyPPT',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Textarea
                    className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    placeholder="Enter address"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="pin_code"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Pin Code</FormLabel>
                  <FormControl>
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="Enter Pin_code"
                      {...field}
                      onChange={(e) => {
                        // Remove non-digit characters except the leading '+'
                        const sanitizedValue = e.target.value.replace(
                          /[^\d+]/g,
                          '',
                        );
                        field.onChange({ target: { value: sanitizedValue } });
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="Enter city"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="Enter state"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="Enter country"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col items-start space-y-2">
            <FormLabel>Contact Information</FormLabel>
            <div className="flex items-center border border-gray-300/70 text-gray-700 rounded-md px-3 w-full">
              <CountryFlag
                countryCode={getCountryFlag(form.watch('countryCode'))}
                svg
                style={{
                  width: '2em',
                  height: '2em',
                  marginRight: '0.5em',
                }}
              />
              <FormField
                control={form.control}
                name="countryCode"
                render={({ field }) => (
                  <FormItem className="flex-shrink-0">
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="new-password"
                        placeholder="+91"
                        onChange={(e) => {
                          // Remove non-digit characters except the leading '+'
                          const sanitizedValue = e.target.value.replace(
                            /[^\d+]/g,
                            '',
                          );
                          field.onChange({ target: { value: sanitizedValue } });
                        }}
                        className="focus:ring-0 outline-none max-w-[60px] text-sm border-none text-black focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="h-6 border-l text-gray-700 border-[#637381] mx-3"></div>
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="flex-grow">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Phone number"
                        onChange={(e) => {
                          // Remove non-digit characters
                          const sanitizedValue = e.target.value.replace(
                            /\D/g,
                            '',
                          );
                          field.onChange({ target: { value: sanitizedValue } });

                          // Attempt to detect as soon as a reasonable number of digits are entered
                          // Adjust the length based on your requirements
                          if (
                            sanitizedValue.length >= 6 &&
                            sanitizedValue.length <= 15
                          ) {
                            detectCountryCodeFromNumber(sanitizedValue);
                          }
                        }}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  w-full text-sm border-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* <FormField
            control={form.control}
            name="type_of_organisation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type of Organization</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple">
                      <SelectValue placeholder="Select from below" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Private">Private</SelectItem>
                    <SelectItem value="Public">Public</SelectItem>
                    <SelectItem value="NGO">NGO</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          /> */}

          <div className="flex gap-3">
            {' '}
            <FormField
              control={form.control}
              name="name_of_sales_person"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Name (Sales Person)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Jaxon Slade"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email_of_sales_person"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Email (Sales Person)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter email"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col items-start space-y-2">
            <FormLabel>Contact Information (Sales Person) </FormLabel>
            <div className="flex items-center border focus:border-lightPurple rounded-md px-3  w-full">
              <CountryFlag
                countryCode={getCountryFlag(form.watch('countryCode'))}
                svg
                style={{
                  width: '2em',
                  height: '2em',
                  marginRight: '0.5em',
                }}
              />
              <FormField
                control={form.control}
                name="salesPersonCountryCode"
                render={({ field }) => (
                  <FormItem className="flex-shrink-0">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="+91"
                        autoComplete="new-password"
                        className="outline-none max-w-[60px] text-sm border-none text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                        onChange={(e) => {
                          // Remove non-digit characters except the leading '+'
                          const sanitizedValue = e.target.value.replace(
                            /[^\d+]/g,
                            '',
                          );
                          field.onChange({ target: { value: sanitizedValue } });
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="h-6 border-l text-[#637381] border-[#637381] mx-3"></div>
              <FormField
                control={form.control}
                name="number_of_sales_person"
                render={({ field }) => (
                  <FormItem className="flex-grow">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Phone number"
                        onChange={(e) => {
                          // Remove non-digit characters
                          const sanitizedValue = e.target.value.replace(
                            /\D/g,
                            '',
                          );
                          field.onChange({ target: { value: sanitizedValue } });

                          // Attempt to detect as soon as a reasonable number of digits are entered
                          // Adjust the length based on your requirements
                          if (
                            sanitizedValue.length >= 6 &&
                            sanitizedValue.length <= 15
                          ) {
                            detectCountryCodeFromNumber(sanitizedValue);
                          }
                        }}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple outline-none w-full text-sm border-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex mt-8 text-lg justify-center">
            <Button
              type="submit"
              disabled={processing}
              className="px-4 py-2 h-12 w-36 bg-darkPurple hover:bg-darkerPurple ease-in duration-300"
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Next
                </span>
              )}
            </Button>
          </div>

          <div className="flex flex-row items-center justify-center space-x-2 ">
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3 w-3" />
                <span className="text-xs text-[#2CD673]">
                  We use industry-standard encryption to protect your data
                </span>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BasicInformationScreen;
